import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import SidePanel, { SidePanelSection, SidePanelDivider } from '@/components/SidePanel';
import { 
  BarChart3, TrendingUp, TrendingDown, IndianRupee, Briefcase, CheckSquare, 
  Clock, AlertTriangle, Calendar, Users, Download, Filter, RefreshCw,
  PieChart, Activity, Target, Award
} from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dateRange, setDateRange] = useState('this_month');
  const [selectedProject, setSelectedProject] = useState('all');
  const [vendorScores, setVendorScores] = useState([]);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [scorecardSending, setScorecardSending] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canAccess = ['owner', 'staff', 'project_manager'].includes(user.role);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, projectsRes, tasksRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/tasks`),
        axios.get(`${API}/payments`).catch(() => ({ data: [] })),
      ]);

      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const fetchVendorScorecard = useCallback(async () => {
    setScorecardLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/email-summaries/vendor-scorecard-preview`, { headers: { Authorization: `Bearer ${token}` } });
      setVendorScores(res.data.vendors || []);
    } catch (e) {
      toast.error('Failed to load scorecard');
    } finally {
      setScorecardLoading(false);
    }
  }, []);

  const handleSendScorecard = async () => {
    setScorecardSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/email-summaries/vendor-scorecard-report`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.email_sent) {
        toast.success(`Scorecard report sent! Top vendor: ${res.data.top_vendor}`);
      } else {
        toast.success('Report generated but email delivery failed.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send scorecard');
    } finally {
      setScorecardSending(false);
    }
  };

  // Calculate metrics
  const calculateMetrics = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    // Filter based on date range
    let filterDate = startOfMonth;
    if (dateRange === 'last_month') {
      filterDate = startOfLastMonth;
    } else if (dateRange === 'this_quarter') {
      const quarter = Math.floor(now.getMonth() / 3);
      filterDate = new Date(now.getFullYear(), quarter * 3, 1);
    } else if (dateRange === 'this_year') {
      filterDate = new Date(now.getFullYear(), 0, 1);
    }

    // Filter by project if selected
    let filteredTasks = tasks;
    let filteredProjects = projects;
    let filteredPayments = payments;

    if (selectedProject !== 'all') {
      filteredTasks = tasks.filter(t => t.project_id === selectedProject);
      filteredProjects = projects.filter(p => p.id === selectedProject);
      filteredPayments = payments.filter(p => p.project_id === selectedProject);
    }

    // Task metrics
    const totalTasks = filteredTasks.length;
    const completedTasks = filteredTasks.filter(t => t.status === 'completed').length;
    const inProgressTasks = filteredTasks.filter(t => t.status === 'in_progress').length;
    const delayedTasks = filteredTasks.filter(t => t.status === 'delayed').length;
    const pendingApproval = filteredTasks.filter(t => t.status === 'pending_approval' || (!t.approved && t.task_cost)).length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // Project metrics
    const activeProjects = filteredProjects.filter(p => p.status === 'in_progress').length;
    const completedProjects = filteredProjects.filter(p => ['completed', 'fully_completed'].includes(p.status)).length;
    const totalBudget = filteredProjects.reduce((sum, p) => sum + (p.budget || 0), 0);
    const spentAmount = filteredTasks.reduce((sum, t) => sum + (t.approved && t.task_cost ? t.task_cost : 0), 0);
    const budgetUtilization = totalBudget > 0 ? Math.round((spentAmount / totalBudget) * 100) : 0;

    // Payment metrics
    const totalPayments = filteredPayments.reduce((sum, p) => sum + p.amount, 0);
    const paidPayments = filteredPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
    const pendingPayments = filteredPayments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
    const overduePayments = filteredPayments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0);

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      delayedTasks,
      pendingApproval,
      completionRate,
      activeProjects,
      completedProjects,
      totalBudget,
      spentAmount,
      budgetUtilization,
      totalPayments,
      paidPayments,
      pendingPayments,
      overduePayments,
    };
  };

  const metrics = calculateMetrics();

  // Get project performance data
  const getProjectPerformance = () => {
    return projects.map(project => {
      const projectTasks = tasks.filter(t => t.project_id === project.id);
      const completedTasks = projectTasks.filter(t => t.status === 'completed').length;
      const totalTasks = projectTasks.length;
      const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      const approvedCost = projectTasks.reduce((sum, t) => sum + (t.approved && t.task_cost ? t.task_cost : 0), 0);
      const budgetUsed = project.budget > 0 ? Math.round((approvedCost / project.budget) * 100) : 0;
      
      return {
        ...project,
        progress,
        completedTasks,
        totalTasks,
        approvedCost,
        budgetUsed,
      };
    }).sort((a, b) => b.progress - a.progress);
  };

  // Get task category breakdown
  const getTaskCategoryBreakdown = () => {
    const categoryMap = {};
    tasks.forEach(task => {
      const category = task.category_name || task.category_group || 'Uncategorized';
      if (!categoryMap[category]) {
        categoryMap[category] = { total: 0, completed: 0, value: 0 };
      }
      categoryMap[category].total++;
      if (task.status === 'completed') categoryMap[category].completed++;
      if (task.approved && task.task_cost) categoryMap[category].value += task.task_cost;
    });

    return Object.entries(categoryMap)
      .map(([name, data]) => ({
        name,
        ...data,
        completion: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  };

  if (!canAccess) {
    return (
      <Layout>
        <div className="text-center py-16">
          <AlertTriangle className="mx-auto mb-4 text-stone-300" size={64} />
          <h2 className="text-xl font-bold text-stone-700 mb-2">Access Restricted</h2>
          <p className="text-stone-500">Reports are available for owners and staff only.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900"></div>
        </div>
      </Layout>
    );
  }

  const projectPerformance = getProjectPerformance();
  const categoryBreakdown = getTaskCategoryBreakdown();

  return (
    <Layout>
      <div className="space-y-8" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-stone-900">Reports Dashboard</h1>
            <p className="text-stone-500 mt-1">Comprehensive analytics and insights</p>
          </div>
          <div className="flex gap-3">
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="this_quarter">This Quarter</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchReportData}>
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Briefcase className="text-blue-600" size={20} />
              </div>
              <span className="text-stone-500 text-sm">Active Projects</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{metrics.activeProjects}</p>
            <p className="text-sm text-stone-400 mt-1">{metrics.completedProjects} completed</p>
          </div>

          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckSquare className="text-green-600" size={20} />
              </div>
              <span className="text-stone-500 text-sm">Task Completion</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{metrics.completionRate}%</p>
            <Progress value={metrics.completionRate} className="h-2 mt-2" />
          </div>

          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <IndianRupee className="text-amber-600" size={20} />
              </div>
              <span className="text-stone-500 text-sm">Budget Utilized</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">₹{(metrics.spentAmount / 100000).toFixed(1)}L</p>
            <p className="text-sm text-stone-400 mt-1">of ₹{(metrics.totalBudget / 100000).toFixed(1)}L total</p>
          </div>

          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <span className="text-stone-500 text-sm">Needs Attention</span>
            </div>
            <p className="text-3xl font-bold text-stone-900">{metrics.delayedTasks + metrics.pendingApproval}</p>
            <p className="text-sm text-stone-400 mt-1">{metrics.delayedTasks} delayed, {metrics.pendingApproval} pending</p>
          </div>
        </div>

        {/* Tabs for different report views */}
        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="projects">Project Performance</TabsTrigger>
            <TabsTrigger value="tasks">Task Analytics</TabsTrigger>
            <TabsTrigger value="financial">Financial Overview</TabsTrigger>
            <TabsTrigger value="vendor_scorecard">Vendor Scorecard</TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <div className="p-6 border-b border-stone-100">
                <h3 className="font-semibold text-stone-900">Project Progress Overview</h3>
                <p className="text-sm text-stone-500 mt-1">Track progress and budget utilization across all projects</p>
              </div>
              <div className="divide-y divide-stone-100">
                {projectPerformance.slice(0, 10).map(project => (
                  <div key={project.id} className="p-6 hover:bg-stone-50 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-stone-900">{project.name}</h4>
                        <p className="text-sm text-stone-500">{project.completedTasks}/{project.totalTasks} tasks completed</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          project.status === 'completed' ? 'bg-green-100 text-green-700' :
                          project.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-stone-100 text-stone-600'
                        }`}>
                          {project.status?.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-stone-500">Progress</span>
                          <span className="font-medium">{project.progress}%</span>
                        </div>
                        <Progress value={project.progress} className="h-2" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-stone-500">Budget Used</span>
                          <span className={`font-medium ${project.budgetUsed > 100 ? 'text-red-600' : ''}`}>
                            {project.budgetUsed}%
                          </span>
                        </div>
                        <Progress 
                          value={Math.min(project.budgetUsed, 100)} 
                          className={`h-2 ${project.budgetUsed > 100 ? '[&>div]:bg-red-500' : ''}`} 
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tasks">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Task Status Distribution */}
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Task Status Distribution</h3>
                <div className="space-y-4">
                  {[
                    { status: 'Completed', count: metrics.completedTasks, color: 'bg-green-500', total: metrics.totalTasks },
                    { status: 'In Progress', count: metrics.inProgressTasks, color: 'bg-blue-500', total: metrics.totalTasks },
                    { status: 'Delayed', count: metrics.delayedTasks, color: 'bg-red-500', total: metrics.totalTasks },
                    { status: 'Pending Approval', count: metrics.pendingApproval, color: 'bg-amber-500', total: metrics.totalTasks },
                  ].map(item => (
                    <div key={item.status}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-stone-600">{item.status}</span>
                        <span className="text-sm font-medium">{item.count}</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${item.color} rounded-full transition-all`}
                          style={{ width: `${item.total > 0 ? (item.count / item.total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Work Category Value</h3>
                <div className="space-y-3">
                  {categoryBreakdown.map((cat, idx) => (
                    <div key={cat.name} className="flex items-center gap-4">
                      <span className="w-6 text-center text-sm font-medium text-stone-400">{idx + 1}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-stone-800">{cat.name}</span>
                          <span className="text-sm text-stone-600">₹{(cat.value / 1000).toFixed(0)}K</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={cat.completion} className="h-1.5 flex-1" />
                          <span className="text-xs text-stone-400 w-12">{cat.completion}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="financial">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Payment Summary */}
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Payment Summary</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-green-700">Paid</span>
                    <span className="font-bold text-green-800">₹{metrics.paidPayments.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <span className="text-amber-700">Pending</span>
                    <span className="font-bold text-amber-800">₹{metrics.pendingPayments.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <span className="text-red-700">Overdue</span>
                    <span className="font-bold text-red-800">₹{metrics.overduePayments.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border-t-2 border-stone-200">
                    <span className="text-stone-700 font-medium">Total</span>
                    <span className="font-bold text-stone-900">₹{metrics.totalPayments.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Budget vs Actual */}
              <div className="bg-white rounded-xl border border-stone-200 p-6 lg:col-span-2">
                <h3 className="font-semibold text-stone-900 mb-4">Budget vs Actual Spending</h3>
                <div className="h-64 flex items-end justify-around gap-4">
                  {projectPerformance.slice(0, 6).map(project => {
                    const budgetHeight = Math.min((project.budget || 0) / (Math.max(...projects.map(p => p.budget || 0)) || 1) * 200, 200);
                    const spentHeight = Math.min((project.approvedCost || 0) / (Math.max(...projects.map(p => p.budget || 0)) || 1) * 200, 200);
                    
                    return (
                      <div key={project.id} className="flex flex-col items-center gap-2 flex-1">
                        <div className="flex items-end gap-1 h-52">
                          <div 
                            className="w-6 bg-blue-200 rounded-t"
                            style={{ height: `${budgetHeight}px` }}
                            title={`Budget: ₹${project.budget?.toLocaleString()}`}
                          />
                          <div 
                            className={`w-6 rounded-t ${project.budgetUsed > 100 ? 'bg-red-500' : 'bg-green-500'}`}
                            style={{ height: `${spentHeight}px` }}
                            title={`Spent: ₹${project.approvedCost?.toLocaleString()}`}
                          />
                        </div>
                        <span className="text-xs text-stone-500 truncate max-w-16 text-center" title={project.name}>
                          {project.name?.slice(0, 8)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-200 rounded" />
                    <span className="text-xs text-stone-500">Budget</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded" />
                    <span className="text-xs text-stone-500">Actual</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Vendor Scorecard Tab */}
          <TabsContent value="vendor_scorecard">
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="vendor-scorecard">
              <div className="p-6 border-b border-stone-100 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-stone-900 flex items-center gap-2"><Award size={18} className="text-amber-600" /> Vendor Scorecard</h3>
                  <p className="text-sm text-stone-500 mt-1">Vendor performance rankings based on payments, projects, quotes, and consistency</p>
                </div>
                <div className="flex gap-2">
                  {vendorScores.length === 0 && (
                    <Button variant="outline" onClick={fetchVendorScorecard} disabled={scorecardLoading} data-testid="load-scorecard-btn">
                      {scorecardLoading ? 'Loading...' : 'Load Scorecard'}
                    </Button>
                  )}
                  {vendorScores.length > 0 && (
                    <>
                      <Button variant="outline" onClick={fetchVendorScorecard} disabled={scorecardLoading}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
                      <Button onClick={handleSendScorecard} disabled={scorecardSending} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="send-scorecard-btn">
                        {scorecardSending ? 'Sending...' : 'Email Report to Admin'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {vendorScores.length === 0 && !scorecardLoading ? (
                <div className="p-16 text-center text-stone-400">
                  <Award size={48} className="mx-auto mb-4 text-stone-300" />
                  <p>Click "Load Scorecard" to generate vendor rankings</p>
                </div>
              ) : scorecardLoading ? (
                <div className="p-16 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900 mx-auto" /></div>
              ) : (
                <div>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4 p-6 border-b border-stone-100">
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{vendorScores.length}</p>
                      <p className="text-xs text-stone-500">Active Vendors</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{vendorScores.length > 0 ? Math.round(vendorScores.reduce((s, v) => s + v.total_score, 0) / vendorScores.length) : 0}</p>
                      <p className="text-xs text-stone-500">Avg Score</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-amber-700">{vendorScores.filter(v => ['A+', 'A'].includes(v.grade)).length}</p>
                      <p className="text-xs text-stone-500">Grade A+ / A</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{vendorScores[0]?.name || '-'}</p>
                      <p className="text-xs text-stone-500">Top Vendor</p>
                    </div>
                  </div>
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-center">#</th>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-left">VENDOR</th>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-center">GRADE</th>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-center">SCORE</th>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-right">TOTAL PAID</th>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-center">PROJECTS</th>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-center">QUOTES</th>
                        <th className="px-4 py-3 text-xs font-semibold text-stone-600 text-center">TREND</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {vendorScores.map((v, idx) => {
                        const gradeColor = ['A+', 'A'].includes(v.grade) ? 'bg-green-600' : v.grade === 'B' ? 'bg-amber-500' : 'bg-red-500';
                        const trendIcon = v.active_months >= 3 ? '↑' : v.active_months === 0 ? '↓' : '—';
                        const trendColor = v.active_months >= 3 ? 'text-green-600' : v.active_months === 0 ? 'text-red-600' : 'text-stone-400';
                        return (
                          <tr key={v.vendor_id} className="hover:bg-stone-50" data-testid={`scorecard-row-${idx}`}>
                            <td className="px-4 py-3 text-center font-bold text-stone-400">{idx + 1}</td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-stone-900">{v.name}</p>
                              {v.company && <p className="text-xs text-stone-400">{v.company}</p>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-block px-2.5 py-1 rounded-full text-white text-xs font-bold ${gradeColor}`}>{v.grade}</span>
                            </td>
                            <td className="px-4 py-3 text-center font-bold text-stone-800">{v.total_score}/100</td>
                            <td className="px-4 py-3 text-right font-medium text-stone-700">{'\u20B9'}{v.total_paid?.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-center text-stone-600">{v.projects}</td>
                            <td className="px-4 py-3 text-center text-stone-600">{v.quotes}</td>
                            <td className={`px-4 py-3 text-center font-bold ${trendColor}`}>{trendIcon}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
